import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Check, RefreshCw, Trash2, ChevronDown, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useDelayedUnmount } from '../hooks/useDelayedUnmount';
import AssetVariantSelector from './storyboard/common/AssetVariantSelector';

/**
 * 图片选择器：横向滚动的缩略图列表，选中高亮，hover 显示大图
 */
const AssetPicker = ({ existingList, selectedId, onChange, theme }) => {
  const [hoverItem, setHoverItem] = useState(null);
  const [lastHoverItem, setLastHoverItem] = useState(null); // 出场动画期间保留数据
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const preview = useDelayedUnmount(!!hoverItem, 150);
  const activeHoverItem = hoverItem || lastHoverItem;

  if (!existingList || existingList.length === 0) {
    return (
      <div className="text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>暂无已有资产</div>
    );
  }

  const handleMouseEnter = (e, item) => {
    if (!item.imageUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverItem(item);
    setLastHoverItem(item);
    setHoverPos({ x: rect.left + rect.width / 2, y: rect.top });
  };

  return (
    <>
      <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
        {existingList.map(item => {
          const isSelected = item.id === selectedId;
          return (
            <button
              key={item.id}
              onClick={() => onChange(item.id)}
              onMouseEnter={(e) => handleMouseEnter(e, item)}
              onMouseLeave={() => setHoverItem(null)}
              className="shrink-0 flex flex-col items-center gap-1 rounded-lg p-1 transition-all"
              style={{
                border: isSelected ? `2px solid ${theme.colors.accentPrimary}` : '2px solid transparent',
                backgroundColor: isSelected ? `${theme.colors.accentPrimary}15` : 'var(--color-bg-overlay)',
              }}
            >
              <div className="relative w-14 h-14 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} className="w-full h-full object-cover" alt={item.name} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg" style={{ color: 'var(--color-text-muted)' }}>?</div>
                )}
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-light)' }}>
                    <Check size={16} style={{ color: theme.colors.accentPrimary }} />
                  </div>
                )}
              </div>
              <span className="text-2xs max-w-[56px] truncate" style={{ color: isSelected ? theme.colors.accentPrimary : 'var(--color-text-secondary)' }}>
                {item.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Hover 大图预览，用 portal 渲染到 body 避免被 overflow:hidden 裁剪 */}
      {preview.mounted && activeHoverItem && activeHoverItem.imageUrl && typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            className={`fixed z-[99999] pointer-events-none ${preview.exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-150'}`}
            style={{
              left: hoverPos.x,
              top: hoverPos.y - 8,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="rounded-xl overflow-hidden shadow-2xl border" style={{ border: '1px solid var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
              <img src={activeHoverItem.imageUrl} className="block" style={{ width: 180, height: 'auto', maxHeight: 280, objectFit: 'contain' }} alt={activeHoverItem.name} />
              <div className="px-3 py-1.5 text-xs font-medium text-center" style={{ color: 'var(--color-text-primary)' }}>
                {activeHoverItem.name}
              </div>
            </div>
            {/* 小三角 */}
            <div className="mx-auto w-0 h-0" style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid var(--color-border-primary)', width: 0 }} />
          </div>,
          document.body
        )
      }
    </>
  );
};

/**
 * 资产确认弹窗
 *
 * Props:
 * - extractedAssets: { characters: [], scenes: [], props: [] }
 * - existingAssets: { characters: [], scenes: [], props: [] }
 * - onConfirm: (confirmedAssets) => void
 * - onCancel: () => void
 */
const AssetConfirmModal = ({ extractedAssets, existingAssets, onConfirm, onCancel }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  // 初始化确认状态
  const [confirmedChars, setConfirmedChars] = useState([]);
  const [confirmedScenes, setConfirmedScenes] = useState([]);
  const [confirmedProps, setConfirmedProps] = useState([]);

  useEffect(() => {
    // 初始化角色
    setConfirmedChars(extractedAssets.characters.map(c => ({
      name: c.name,
      imagePrompt: c.imagePrompt,
      action: c.status === 'exact' ? 'reuse' : 'generate',
      reuseId: c.status === 'exact' ? c.similarTo?.id : null,
      variantIndex: -1, // 默认使用主体图
      status: c.status,
      similarTo: c.similarTo,
      similarity: c.similarity
    })));

    // 初始化场景
    setConfirmedScenes(extractedAssets.scenes.map(s => ({
      name: s.name,
      imagePrompt: s.imagePrompt,
      action: s.status === 'exact' ? 'reuse' : 'generate',
      reuseId: s.status === 'exact' ? s.similarTo?.id : null,
      variantIndex: -1, // 默认使用主体图
      status: s.status,
      similarTo: s.similarTo,
      similarity: s.similarity
    })));

    // 初始化道具
    setConfirmedProps(extractedAssets.props.map(p => ({
      name: p.name,
      imagePrompt: p.imagePrompt,
      action: p.status === 'exact' ? 'reuse' : 'generate',
      reuseId: p.status === 'exact' ? p.similarTo?.id : null,
      variantIndex: -1, // 默认使用主体图
      status: p.status,
      similarTo: p.similarTo,
      similarity: p.similarity
    })));
  }, [extractedAssets]);

  const handleConfirm = () => {
    onConfirm({
      characters: confirmedChars,
      scenes: confirmedScenes,
      props: confirmedProps
    });
  };

  // 批量操作：全部新建生图
  const setAllGenerate = () => {
    setConfirmedChars(prev => prev.map(c => c.action === 'skip' ? c : { ...c, action: 'generate' }));
    setConfirmedScenes(prev => prev.map(s => s.action === 'skip' ? s : { ...s, action: 'generate' }));
    setConfirmedProps(prev => prev.map(p => p.action === 'skip' ? p : { ...p, action: 'generate' }));
  };

  // 批量操作：全部复用已有（有匹配的复用，没有的保持生成）
  const setAllReuse = () => {
    setConfirmedChars(prev => prev.map(c => c.action === 'skip' ? c : {
      ...c,
      action: c.similarTo?.id ? 'reuse' : 'generate',
      reuseId: c.similarTo?.id || null,
    }));
    setConfirmedScenes(prev => prev.map(s => s.action === 'skip' ? s : {
      ...s,
      action: s.similarTo?.id ? 'reuse' : 'generate',
      reuseId: s.similarTo?.id || null,
    }));
    setConfirmedProps(prev => prev.map(p => p.action === 'skip' ? p : {
      ...p,
      action: p.similarTo?.id ? 'reuse' : 'generate',
      reuseId: p.similarTo?.id || null,
    }));
  };

  const updateAsset = (type, index, updates) => {
    const setter = type === 'character' ? setConfirmedChars : type === 'scene' ? setConfirmedScenes : setConfirmedProps;
    setter(prev => {
      const newList = [...prev];
      newList[index] = { ...newList[index], ...updates };
      return newList;
    });
  };

  const removeAsset = (type, index) => {
    const setter = type === 'character' ? setConfirmedChars : type === 'scene' ? setConfirmedScenes : setConfirmedProps;
    setter(prev => {
      const newList = [...prev];
      newList[index] = { ...newList[index], action: 'skip' };
      return newList;
    });
  };

  const renderAssetCard = (asset, index, type, existingList) => {
    if (asset.action === 'skip') return null;

    const isNew = asset.status === 'new';
    const isSimilar = asset.status === 'similar';
    const isExact = asset.status === 'exact';

    return (
      <div
        key={index}
        className="rounded-lg p-3 border"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: isSimilar ? theme.colors.accentPrimary : 'var(--color-border-primary)'
        }}
      >
        {/* 头部：状态标签 + 删除按钮 */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isNew && (
              <span className="text-2xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ backgroundColor: `${theme.colors.greenPrimary}20`, color: theme.colors.greenPrimary }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.colors.greenPrimary }} /> 新建
              </span>
            )}
            {isSimilar && (
              <span className="text-2xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }} /> 疑似重复 ({Math.round(asset.similarity * 100)}%)
              </span>
            )}
            {isExact && (
              <span className="text-2xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ backgroundColor: `${theme.colors.bluePrimary}20`, color: theme.colors.bluePrimary }}>
                <CheckCircle2 size={10} /> 自动复用
              </span>
            )}
            <span className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>{asset.name}</span>
          </div>
          <button
            onClick={() => removeAsset(type, index)}
            className="p-1 rounded hover:bg-[var(--color-bg-tertiary)] transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            title="跳过此资产"
          >
            <Trash2 size={14} />
          </button>
        </div>

        {/* 疑似重复：显示已有资产对比 */}
        {isSimilar && asset.similarTo && (
          <div className="mb-2 p-2 rounded" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
            <div className="text-2xs mb-1" style={{ color: 'var(--color-text-muted)' }}>已有资产：</div>
            <div className="flex items-center gap-2">
              {asset.similarTo.imageUrl && (
                <img src={asset.similarTo.imageUrl} className="w-12 h-12 rounded object-cover" alt={asset.similarTo.name} />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{asset.similarTo.name}</div>
                <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>{asset.similarTo.description}</div>
              </div>
            </div>
          </div>
        )}

        {/* imagePrompt 编辑框 */}
        {asset.action === 'generate' && (
          <div className="mb-2">
            <label className="text-2xs mb-1 block" style={{ color: 'var(--color-text-muted)' }}>生成提示词：</label>
            <textarea
              value={asset.imagePrompt}
              onChange={(e) => updateAsset(type, index, { imagePrompt: e.target.value })}
              className="w-full text-xs p-2 rounded border outline-none resize-none"
              style={{
                backgroundColor: 'var(--color-bg-overlay)',
                borderColor: 'var(--color-border-primary)',
                color: 'var(--color-text-secondary)'
              }}
              rows={3}
            />
          </div>
        )}

        {/* 操作选择 */}
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              checked={asset.action === 'generate'}
              onChange={() => updateAsset(type, index, { action: 'generate', reuseId: null })}
              style={{ accentColor: theme.colors.accentPrimary }}
            />
            <span style={{ color: 'var(--color-text-secondary)' }}>新建并生图</span>
          </label>

          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              checked={asset.action === 'reuse'}
              onChange={() => {
                const defaultId = asset.similarTo?.id || existingList[0]?.id;
                updateAsset(type, index, { action: 'reuse', reuseId: defaultId });
              }}
              style={{ accentColor: theme.colors.bluePrimary }}
            />
            <span style={{ color: 'var(--color-text-secondary)' }}>指定为已有</span>
          </label>
        </div>

        {/* 选中已有时：显示图片选择器 */}
        {asset.action === 'reuse' && (
          <>
            <AssetPicker
              existingList={existingList}
              selectedId={asset.reuseId}
              onChange={(id) => updateAsset(type, index, { reuseId: id, variantIndex: -1 })}
              theme={theme}
            />

            {/* 变体选择器 */}
            {asset.reuseId && (
              <div className="mt-2">
                <label className="text-2xs mb-1 block" style={{ color: 'var(--color-text-muted)' }}>选择变体：</label>
                <AssetVariantSelector
                  asset={existingList.find(a => a.id === asset.reuseId)}
                  selectedVariantIndex={asset.variantIndex ?? -1}
                  onChange={(variantIndex) => updateAsset(type, index, { variantIndex })}
                  size="sm"
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const totalNew = [...confirmedChars, ...confirmedScenes, ...confirmedProps].filter(a => a.action === 'generate').length;
  const totalReuse = [...confirmedChars, ...confirmedScenes, ...confirmedProps].filter(a => a.action === 'reuse').length;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" style={{ backgroundColor: 'var(--overlay-heavy)' }}>
      <div
        className="w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)' }}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>资产确认</h2>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              分镜已生成，请确认以下资产后开始生图
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={setAllGenerate}
              className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
              style={{
                backgroundColor: `${theme.colors.accentPrimary}15`,
                color: theme.colors.accentPrimary,
                border: `1px solid ${theme.colors.accentPrimary}40`,
              }}
            >
              <RefreshCw size={12} />全部新建生图
            </button>
            <button
              onClick={setAllReuse}
              className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
              style={{
                backgroundColor: `${theme.colors.bluePrimary}15`,
                color: theme.colors.bluePrimary,
                border: `1px solid ${theme.colors.bluePrimary}40`,
              }}
            >
              <Check size={12} />全部复用已有
            </button>
            <button
              onClick={onCancel}
              className="p-2 rounded-full hover:bg-[var(--color-bg-tertiary)] transition-all duration-200"
              style={{ color: 'var(--color-text-muted)' }}
            >
            <X size={20} />
          </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-6">
            {/* 角色列 */}
            <div>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                角色 ({confirmedChars.filter(c => c.action !== 'skip').length})
              </h3>
              <div className="space-y-3">
                {confirmedChars.map((char, i) => renderAssetCard(char, i, 'character', existingAssets.characters))}
              </div>
            </div>

            {/* 场景列 */}
            <div>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                场景 ({confirmedScenes.filter(s => s.action !== 'skip').length})
              </h3>
              <div className="space-y-3">
                {confirmedScenes.map((scene, i) => renderAssetCard(scene, i, 'scene', existingAssets.scenes))}
              </div>
            </div>

            {/* 道具列 */}
            <div>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                道具 ({confirmedProps.filter(p => p.action !== 'skip').length})
              </h3>
              <div className="space-y-3">
                {confirmedProps.map((prop, i) => renderAssetCard(prop, i, 'prop', existingAssets.props))}
              </div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="flex items-center justify-between px-6 py-4 border-t shrink-0" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            将生成 <span className="font-bold" style={{ color: theme.colors.accentPrimary }}>{totalNew}</span> 个新资产，
            复用 <span className="font-bold" style={{ color: theme.colors.bluePrimary }}>{totalReuse}</span> 个已有资产
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              style={{ backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-secondary)' }}
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              className="px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2"
              style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
            >
              <RefreshCw size={14} />
              确认，开始生图
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetConfirmModal;
